import './sliders.js';
import './events-venues.js';
import './gsap.min.js';
import './ScrollTrigger.min.js';
// import './tiffin-interaction.js';

import { requestUserLocation, getStoredLocation, getStoredLocationId, setStoredLocationId, haversineDistance, getNearestRestaurant, getStoredLocationSource } from './location.js';
import { initOffcanvas } from './offcanvas.js';
import { initChoicesConfig } from './choices-config.js';
import { initBasket } from './basket.js';
import { initCheckout } from './checkout.js';
import { initBilling } from './billing.js';


document.addEventListener('DOMContentLoaded', function () {

	// Initialize GSAP animations
	gsap.registerPlugin(ScrollTrigger);

	// ── GSAP Animations ──────────────────────────────────────────────────────
	// Reveal animation for elements with .reveal class
	gsap.utils.toArray('.reveal').forEach((el) => {
		gsap.from(el, {
			opacity: 0,
			y: 30,
			duration: 0.7,
			ease: 'power2.out',
			scrollTrigger: {
				trigger: el,
				start: 'top 85%',
				toggleActions: 'play none none none',
			}
		});
	});

	// Staggered reveal for elements with .stagger class inside .stagger-container
	gsap.from('.stagger', {
		opacity: 0,
		y: 40,
		duration: 0.8,
		stagger: 0.12,
		ease: 'power2.out',
		scrollTrigger: {
			trigger: '.stagger-container',
			start: 'top 80%',
		}
	});

	// Directional reveal for left
	gsap.from('.reveal-left', {
		opacity: 0,
		x: -30,
		duration: 1.7,
		scrollTrigger: {
			trigger: '.reveal-left',
			start: 'top 85%',
		}
	});

	// Directional reveal for right
	gsap.from('.reveal-right', {
		opacity: 0,
		x: 30,
		duration: 1.7,
		scrollTrigger: {
			trigger: '.reveal-right',
			start: 'top 85%',
		}
	});

	// ── Nearest Restaurants ──────────────────────────────────────────────────────
	function applyNearestRestaurants(userCoords) {
		// Rebuild the restaurant slider sorted by distance from the user's coords.

		const dataScript = document.getElementById('front-page-restaurant-data');
		if (!dataScript) return null;

		const restaurants = JSON.parse(dataScript.textContent);
		const withCoords = restaurants.filter((r) => r.lat !== null && r.lng !== null);
		if (!withCoords.length) return null;

		const sorted = withCoords
			.map((r) => ({ ...r, distanceMiles: haversineDistance(userCoords.lat, userCoords.lng, r.lat, r.lng) }))
			.sort((a, b) => a.distanceMiles - b.distanceMiles)
			.slice(0, 3);

		// Persist the nearest restaurant and sync all location-selector UI
		if (sorted.length) {
			setStoredLocationId(sorted[0].id);

			// Delegate to the location-selector module so Choices.js, button states,
			// menu links and the mowgli_location_changed event are all updated correctly.
			if (window.mowgliLocation && window.mowgliLocation.save) {
				window.mowgliLocation.save(String(sorted[0].id), sorted[0].title, 'geolocation');
			} else {
				document.querySelectorAll('.js-location-select').forEach(sel => {
					sel.value = String(sorted[0].id);
				});
			}
		}

		const sliderEl = document.querySelector('.js-restaurant-slider');
		const wrapper = sliderEl && sliderEl.querySelector('.swiper-wrapper');
		if (wrapper) {
			wrapper.innerHTML = sorted.map((r) => {
				const distLabel = r.distanceMiles < 0.1
					? '< 0.1 miles away'
					: `${r.distanceMiles.toFixed(1)} miles away`;

				const slide = document.createElement('a');
				slide.className = 'swiper-slide border-radius';
				slide.href = r.permalink;
				slide.style.cssText = `background: url('${r.image}'); background-size: cover; background-position: center;`;

				const content = document.createElement('div');
				content.className = 'swiper-slide-content';

				const label = document.createElement('p');
				label.className = 'text-uppercase text-light';
				label.textContent = distLabel;

				const title = document.createElement('h3');
				title.className = 'size-lg-4 text-light';
				title.textContent = r.title;

				content.appendChild(label);
				content.appendChild(title);
				slide.appendChild(content);

				return slide.outerHTML;
			}).join('');

			if (sliderEl.swiper) {
				sliderEl.swiper.update();
				sliderEl.swiper.slideTo(0);
			}
		}

		return sorted[0] || null;
	}

	// ── Tiffin interaction ──────────────────────────────────────────────────────
	function initTiffinInteraction() {
		const tiffinWrap    = document.getElementById('tiffin');
		const tiffinBadge   = document.getElementById('tapBadge');
		const tiffinTierRows = document.querySelectorAll('.tier-row');

		if (tiffinWrap) {
			let isTiffinOpen   = false;
			let activeTierRow  = null;

			tiffinWrap.addEventListener('click', () => {
				if (!isTiffinOpen) {
					isTiffinOpen = true;
					tiffinWrap.classList.replace('closed', 'open');
				}
			});

			if (tiffinBadge) {
				tiffinBadge.addEventListener('click', (e) => {
					e.stopPropagation();
					isTiffinOpen = false;
					tiffinWrap.classList.replace('open', 'closed');
					if (activeTierRow) { activeTierRow.classList.remove('active'); activeTierRow = null; }
					tiffinWrap.classList.remove('active-0', 'active-1', 'active-2');
				});
			}

			tiffinTierRows.forEach(row => {
				row.addEventListener('click', (e) => {
					if (!isTiffinOpen) return;
					e.stopPropagation();

					if (activeTierRow === row) {
						row.classList.remove('active');
						tiffinWrap.classList.remove('active-0', 'active-1', 'active-2');
						activeTierRow = null;
						return;
					}

					if (activeTierRow) activeTierRow.classList.remove('active');
					activeTierRow = row;
					row.classList.add('active');
					tiffinWrap.classList.remove('active-0', 'active-1', 'active-2');
					tiffinWrap.classList.add('active-' + row.dataset.idx);
				});
			});
		}
	}

	// Auto-apply on load only when the last selection was geolocation-based.
	// If the user manually chose a location (source === 'manual'), leave it untouched.
	const cachedCoords = getStoredLocation();

	if (cachedCoords && getStoredLocationSource() === 'geolocation') {
		applyNearestRestaurants(cachedCoords);
		document.querySelectorAll('.js-use-location').forEach(btn => btn.classList.add('is-active'));
	}

	// "Use location" button – request permission then sort the slider
	document.querySelectorAll('.js-use-location').forEach(useLocationBtn => {
		useLocationBtn.addEventListener('click', async (event) => {
			event.preventDefault();

			useLocationBtn.classList.add('is-loading');
			useLocationBtn.setAttribute('aria-busy', 'true');

			try {
				// Force a fresh geolocation request so distance is always recalculated.
				const userCoords = await requestUserLocation(true);
				const nearest = applyNearestRestaurants(userCoords);
				document.querySelectorAll('.js-use-location').forEach(btn => btn.classList.add('is-active'));
				if (nearest && nearest.permalink) {
					window.location.href = nearest.permalink;
				}
			} catch (error) {
				console.warn('Location request failed:', error.message);
			} finally {
				useLocationBtn.classList.remove('is-loading');
				useLocationBtn.removeAttribute('aria-busy');
			}
		});
	});


	// ── Scroll to top ──────────────────────────────────────────────────────
	const scrollTopBtn = document.querySelector('.js-scroll-to-top');
	if (scrollTopBtn) {
		const toggleScrollTop = () => {
			scrollTopBtn.classList.toggle('is-visible', window.scrollY > 300);
		};
		toggleScrollTop();
		window.addEventListener('scroll', toggleScrollTop, { passive: true });
		scrollTopBtn.addEventListener('click', () => {
			window.scrollTo({ top: 0, behavior: 'smooth' });
		});
	}

	// ── Sustainability card toggles ────────────────────────────────────────
	if (window.jQuery) {
		const $ = window.jQuery;
		const openLabel = 'Close';
		const closedLabel = 'Discover';

		$('.sustainability-card').each(function () {
			const $card = $(this);
			const $button = $card.find('button[aria-expanded]').first();
			const $content = $card.find('.sustainability-card-content').first();

			if (!$button.length || !$content.length) {
				return;
			}

			$button.text(closedLabel).attr('aria-expanded', 'false');
			$content.hide();

			$button.on('click', function (event) {
				event.preventDefault();

				$content.stop(true, true).slideToggle(250, function () {
					const isNowOpen = $content.is(':visible');
					$button
						.attr('aria-expanded', isNowOpen ? 'true' : 'false')
						.text(isNowOpen ? openLabel : closedLabel);
				});
			});
		});
	}

	// Run on load //
	initOffcanvas();
	initChoicesConfig();
	initBasket();
	initCheckout();
	initBilling();
	initTiffinInteraction();

});
